// Gateways: provisionamento pela tela (só o admin da plataforma). Criar gera a credencial
// MQTT no broker e mostra UMA vez o config.yaml pronto para o gateway; trocar senha e revogar
// agem na hora. O banco não guarda a senha — perdeu, gera outra.

import { useQuery } from "@tanstack/react-query";
import { Activity, CircleOff, Copy, Download, KeyRound, Plus, RadioTower, ShieldX, Trash2 } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { Alert, Badge, Button, EmptyState, Field, Input, Modal, PageHeader, Select, Spinner, Table } from "../../components/ui";
import { api, type Tree } from "../../lib/api";
import { useAction, useTree } from "../../lib/hooks";

interface GatewayRow {
  id: number;
  gateway_id: string;
  online: boolean | null;
  version: string | null;
  regulators: string[] | null;
  last_seen: string | null;
  mqtt_username: string | null;
  provisioned_at: string | null;
  password_changed_at: string | null;
  provisioned_by_name: string | null;
  site_id: number;
  site_slug: string;
  site_name: string;
  group_name: string;
}

interface Credential {
  id?: number;
  username: string;
  password: string;
  site: string;
  gatewayId: string;
  broker: { host: string; port: number; tls: boolean };
}

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

export function GatewaysPage() {
  const list = useQuery({
    queryKey: ["gateways"],
    queryFn: () => api.get<{ enabled: boolean; gateways: GatewayRow[] }>("/api/admin/gateways"),
    refetchInterval: 30_000,
  });
  const { data: tree } = useTree();
  const [creating, setCreating] = useState(false);
  const [shown, setShown] = useState<{ credential: Credential; title: string } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: ConfirmKind; gw: GatewayRow } | null>(null);

  return (
    <>
      <PageHeader
        title="Gateways"
        description="Credenciais MQTT dos gateways das usinas. Cada gateway só publica nos tópicos da própria usina."
        actions={
          list.data?.enabled && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden /> Novo gateway
            </Button>
          )
        }
      />
      {list.data && !list.data.enabled && (
        <div className="mb-4">
          <Alert>O provisionamento pelo broker não está configurado nesta instalação (MQTT_ADMIN_URL/MQTT_ADMIN_PASSWORD).</Alert>
        </div>
      )}
      <Installers />

      {list.isLoading ? (
        <Spinner />
      ) : !list.data?.gateways.length ? (
        <EmptyState title="Nenhum gateway">Crie o primeiro para gerar a credencial e o config.yaml.</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Gateway</th>
              <th>Usina</th>
              <th>Situação</th>
              <th>Credencial</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data.gateways.map((g) => (
              <tr key={g.id}>
                <td>
                  <p className="flex items-center gap-2 font-medium">
                    <RadioTower className="size-4 text-muted" aria-hidden /> {g.gateway_id}
                  </p>
                  {g.version && <p className="text-xs text-muted">v{g.version}</p>}
                </td>
                <td className="text-sm">
                  {g.site_name}
                  <p className="text-xs text-muted">
                    {g.group_name} · <code className="font-mono">{g.site_slug}</code>
                  </p>
                </td>
                <td>
                  {g.online ? (
                    <Badge tone="ok">
                      <Activity className="size-3" aria-hidden /> online
                    </Badge>
                  ) : g.online === false ? (
                    <Badge tone="warn">
                      <CircleOff className="size-3" aria-hidden /> offline
                    </Badge>
                  ) : (
                    <Badge>nunca conectou</Badge>
                  )}
                  <p className="mt-1 text-xs text-muted">último contato {fmt(g.last_seen)}</p>
                </td>
                <td className="text-sm">
                  {g.mqtt_username ? (
                    <>
                      <code className="font-mono text-xs">{g.mqtt_username}</code>
                      <p className="text-xs text-muted">
                        senha de {fmt(g.password_changed_at ?? g.provisioned_at)}
                        {g.provisioned_by_name && ` · por ${g.provisioned_by_name}`}
                      </p>
                    </>
                  ) : (
                    <Badge tone="danger">revogada</Badge>
                  )}
                </td>
                <td className="whitespace-nowrap text-right">
                  <div className="flex justify-end gap-1">
                    {list.data!.enabled && (
                      <Button size="sm" variant="secondary" onClick={() => setConfirm({ kind: "password", gw: g })}>
                        <KeyRound className="size-3.5" aria-hidden /> {g.mqtt_username ? "Trocar senha" : "Nova credencial"}
                      </Button>
                    )}
                    {list.data!.enabled && g.mqtt_username && (
                      <Button size="sm" variant="secondary" onClick={() => setConfirm({ kind: "revoke", gw: g })}>
                        <ShieldX className="size-3.5" aria-hidden /> Revogar
                      </Button>
                    )}
                    {/* Só sem credencial: com ela, o gateway seguiria publicando. Não passa pelo broker. */}
                    {!g.mqtt_username && (
                      <Button size="sm" variant="secondary" onClick={() => setConfirm({ kind: "delete", gw: g })}>
                        <Trash2 className="size-3.5" aria-hidden /> Apagar
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {creating && tree && (
        <CreateGatewayDialog
          tree={tree}
          taken={list.data?.gateways ?? []}
          onClose={() => setCreating(false)}
          onCreated={(c) => {
            setCreating(false);
            setShown({ credential: c, title: `Gateway ${c.gatewayId} criado` });
          }}
        />
      )}
      {confirm && (
        <ConfirmDialog
          {...confirm}
          onClose={() => setConfirm(null)}
          onCredential={(c) => {
            setConfirm(null);
            setShown({ credential: c, title: `Nova senha do gateway ${c.gatewayId}` });
          }}
        />
      )}
      {shown && tree && <CredentialDialog {...shown} tree={tree} onClose={() => setShown(null)} />}
    </>
  );
}

interface Installer {
  name: string;
  platform: "windows" | "linux";
  version: string;
  size: number;
  modified: string;
  sha256: string;
}

const PLATFORM = {
  windows: { label: "Windows", hint: "Setup com assistente: instala o serviço “GRTD Gateway”. Não precisa de Python nem de internet." },
  linux: { label: "Linux (Ubuntu)", hint: "Pacote para a caixa dedicada: deploy/network-setup.sh e deploy/install.sh --config." },
} as const;

/** O instalador mais recente de cada sistema; os anteriores ficam num "ver todos". */
function Installers() {
  const q = useQuery({
    queryKey: ["gateway-installers"],
    queryFn: () => api.get<{ enabled: boolean; files: Installer[] }>("/api/admin/gateways/installers"),
  });
  const [all, setAll] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  if (!q.data?.enabled) return null;
  const files = q.data.files;
  const latest = (["windows", "linux"] as const).map((p) => files.find((f) => f.platform === p)).filter((f): f is Installer => !!f);
  const shown = all ? files : latest;

  const copyHash = async (f: Installer) => {
    try {
      await navigator.clipboard.writeText(f.sha256);
      setCopied(f.name);
    } catch {
      setCopied(null);
    }
  };

  return (
    <section className="mb-6 rounded-lg border border-line bg-surface p-4" aria-labelledby="inst-titulo">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="inst-titulo" className="font-semibold">
          Instaladores do gateway
        </h2>
        {files.length > latest.length && (
          <button type="button" onClick={() => setAll(!all)} className="text-sm text-brand hover:underline">
            {all ? "Só os mais recentes" : `Ver todos (${files.length})`}
          </button>
        )}
      </div>
      {!files.length ? (
        <p className="text-sm text-muted">
          Nenhum instalador publicado. Gere com <code className="font-mono">build-setup.ps1</code> / <code className="font-mono">make-bundle</code> e envie para{" "}
          <code className="font-mono">deploy/downloads/</code> na VPS.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {shown.map((f) => (
            <li key={f.name} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  <span className="font-medium">{PLATFORM[f.platform].label}</span> <span className="text-muted">versão {f.version}</span>
                  {latest.includes(f) && files.length > latest.length && <span className="ml-2"><Badge tone="ok">mais recente</Badge></span>}
                </p>
                <p className="text-xs text-muted">{PLATFORM[f.platform].hint}</p>
                <p className="mt-0.5 text-xs text-muted tabular-nums">
                  {(f.size / 1048576).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB · {new Date(f.modified).toLocaleString("pt-BR")} · SHA-256{" "}
                  <button type="button" onClick={() => copyHash(f)} className="font-mono hover:text-fg" title="Copiar o SHA-256 completo">
                    {f.sha256.slice(0, 12)}…{copied === f.name ? " (copiado)" : ""}
                  </button>
                </p>
              </div>
              <a
                href={`/api/admin/gateways/installers/${encodeURIComponent(f.name)}`}
                download={f.name}
                className="inline-flex h-9 items-center gap-2 rounded-md bg-brand px-3 text-sm font-medium text-white hover:brightness-110"
              >
                <Download className="size-4" aria-hidden /> Baixar
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CreateGatewayDialog({
  tree,
  taken,
  onClose,
  onCreated,
}: {
  tree: Tree;
  taken: GatewayRow[];
  onClose: () => void;
  onCreated: (c: Credential) => void;
}) {
  const sites = useMemo(() => tree.groups.flatMap((g) => g.sites.map((s) => ({ ...s, group: g.name }))), [tree]);
  const [siteId, setSiteId] = useState(String(sites[0]?.id ?? ""));
  // Sugere o próximo gw<n> livre da usina.
  const next = (sid: string) => {
    const used = new Set(taken.filter((g) => String(g.site_id) === sid).map((g) => g.gateway_id));
    let n = 1;
    while (used.has(`gw${n}`)) n++;
    return `gw${n}`;
  };
  const [gatewayId, setGatewayId] = useState(() => next(String(sites[0]?.id ?? "")));
  const { run, busy, error } = useAction([["gateways"]]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    let c: Credential | null = null;
    await run(async () => {
      c = await api.post<Credential>(`/api/admin/sites/${siteId}/gateways`, { gatewayId });
    });
    if (c) onCreated(c);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Novo gateway"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="new-gateway" loading={busy} disabled={!siteId}>
            Criar credencial
          </Button>
        </>
      }
    >
      <form id="new-gateway" onSubmit={submit} className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Usina">
          {(id) => (
            <Select
              id={id}
              value={siteId}
              onChange={(e) => {
                setSiteId(e.target.value);
                setGatewayId(next(e.target.value));
              }}
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.group} › {s.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Identificador do gateway" hint="Minúsculas, dígitos e hífen. Vai no config.yaml (gateway_id) e no nome do usuário MQTT.">
          {(id) => (
            <Input
              id={id}
              value={gatewayId}
              onChange={(e) => setGatewayId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              required
              pattern="[a-z0-9][a-z0-9-]*"
              className="font-mono"
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}

type ConfirmKind = "password" | "revoke" | "delete";

function ConfirmDialog({
  kind,
  gw,
  onClose,
  onCredential,
}: {
  kind: ConfirmKind;
  gw: GatewayRow;
  onClose: () => void;
  onCredential: (c: Credential) => void;
}) {
  const { run, busy, error } = useAction([["gateways"]]);
  const revoke = kind === "revoke";
  const remove = kind === "delete";
  return (
    <Modal
      open
      onClose={onClose}
      title={
        remove
          ? `Apagar ${gw.gateway_id}`
          : revoke
            ? `Revogar ${gw.gateway_id}`
            : gw.mqtt_username
              ? `Trocar senha de ${gw.gateway_id}`
              : `Nova credencial para ${gw.gateway_id}`
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant={revoke || remove ? "danger" : "primary"}
            loading={busy}
            onClick={async () => {
              if (remove) {
                if (await run(() => api.del(`/api/admin/gateways/${gw.id}`))) onClose();
              } else if (revoke) {
                if (await run(() => api.del(`/api/admin/gateways/${gw.id}/credential`))) onClose();
              } else {
                let c: Credential | null = null;
                await run(async () => {
                  c = await api.post<Credential>(`/api/admin/gateways/${gw.id}/password`);
                });
                if (c) onCredential(c);
              }
            }}
          >
            {remove ? "Apagar" : revoke ? "Revogar" : "Gerar senha"}
          </Button>
        </>
      }
    >
      {error && <Alert kind="error">{error}</Alert>}
      <p className="text-sm">
        {remove ? (
          <>
            O cadastro do gateway <strong>{gw.gateway_id}</strong> da usina {gw.site_name} sai da lista, junto com o histórico de conexão
            dele. Os dados dos reguladores ficam. Para usar o mesmo nome de novo, crie o gateway outra vez.
          </>
        ) : revoke ? (
          <>
            O gateway <strong>{gw.gateway_id}</strong> da usina {gw.site_name} deixa de conectar na hora e os dados dele param de chegar. O
            histórico fica. Para voltar, gere uma nova credencial e atualize o gateway.
          </>
        ) : gw.mqtt_username ? (
          <>
            A senha atual para de valer na hora: o gateway <strong>{gw.gateway_id}</strong> desconecta até receber a nova no config.yaml.
          </>
        ) : (
          <>Gera uma credencial nova para o gateway revogado.</>
        )}
      </p>
    </Modal>
  );
}

/** config.yaml completo, no formato do config.example.yaml do gateway, já com a credencial. */
function configYaml(c: Credential, unit: string, regulators: string[]): string {
  const regs = (regulators.length ? regulators : ["reg1"])
    .map((r, i) => `  - name: ${r}\n    host: 192.168.0.${i + 1}          # AJUSTE: IP do GRTD-2000\n    port: 502\n    unit_id: 1`)
    .join("\n");
  return `# Gerado pela plataforma GMeyer Insight em ${new Date().toLocaleString("pt-BR")}.
# Contém a senha do gateway: guarde com acesso restrito (Linux: chmod 600; Windows: só o usuário do serviço).
site: ${c.site}
generating_unit: ${unit}
gateway_id: ${c.gatewayId}
topic_prefix: grtd

regulators:
${regs}

broker:
  host: ${c.broker.host}
  port: ${c.broker.port}
  tls: ${c.broker.tls}
  keepalive: 60
  username: ${c.username}
  password: "${c.password}"

intervals:
  electrical: 2.0
  status: 5.0
  alarms: 1.0
  meta: 300.0
  reconnect_min: 2.0
  reconnect_max: 60.0

telemetry:
  electrical:
    - [0, 26]
  status:
    - [40, 52]
  decode_bits: true

files:
  enabled: true
  interval: 60.0
  download_dir: /var/lib/grtd-gateway/files     # Windows: %LOCALAPPDATA%\\grtd-gateway\\files
  state_db: /var/lib/grtd-gateway/state.db
  max_publish_bytes: 1000000
  max_files_per_sweep: 5
  keep_local: false
  folders:
    - path: /OSC
      kind: osc
      extensions: ['.txt']
      max_size: 200000
    - path: /EVT
      kind: evt
      extensions: ['.txt']
      max_size: 600000

memory_queue_max: 5000

outbox:
  persist: true
  path: /var/lib/grtd-gateway/outbox.db
  max_rows: 20000
`;
}

function CredentialDialog({ credential: c, title, tree, onClose }: { credential: Credential; title: string; tree: Tree; onClose: () => void }) {
  // UGs e reguladores da usina, para preencher generating_unit e a lista de reguladores.
  const units = useMemo(() => tree.groups.flatMap((g) => g.sites).find((s) => s.slug === c.site)?.units ?? [], [tree, c.site]);
  const [unit, setUnit] = useState(units[0]?.slug ?? "ug1");
  const regs = units.find((u) => u.slug === unit)?.regulators.map((r) => r.slug) ?? [];
  const yaml = configYaml(c, unit, regs);
  const env = `GRTD_MQTT_USERNAME=${c.username}\nGRTD_MQTT_PASSWORD=${c.password}\n`;
  const file = `config-${c.site}-${c.gatewayId}.yaml`;
  const installCmd = `# Linux (Ubuntu)\nsudo bash deploy/network-setup.sh\nsudo bash deploy/install.sh --config ~/${file}\n\n# Windows (PowerShell como Administrador)\npowershell -ExecutionPolicy Bypass -File deploy\\install.ps1 -Config $HOME\\Downloads\\${file}`;
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (what: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch {
      setCopied(null);
    }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([yaml], { type: "text/yaml" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `config-${c.site}-${c.gatewayId}.yaml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal open onClose={onClose} title={title} footer={<Button onClick={onClose}>Fechar</Button>}>
      <Alert kind="ok">
        Usuário <code className="font-mono">{c.username}</code>. A senha aparece só agora — baixe o config.yaml ou copie antes de fechar.
      </Alert>
      {units.length > 1 && (
        <Field label="Unidade geradora deste gateway">
          {(id) => (
            <Select id={id} value={unit} onChange={(e) => setUnit(e.target.value)}>
              {units.map((u) => (
                <option key={u.id} value={u.slug}>
                  {u.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      <div className="flex flex-wrap gap-2">
        <Button onClick={download}>
          <Download className="size-4" aria-hidden /> Baixar config.yaml
        </Button>
        <Button variant="secondary" onClick={() => copy("yaml", yaml)}>
          <Copy className="size-4" aria-hidden /> {copied === "yaml" ? "Copiado" : "Copiar config.yaml"}
        </Button>
        <Button variant="secondary" onClick={() => copy("pass", c.password)}>
          <KeyRound className="size-4" aria-hidden /> {copied === "pass" ? "Copiada" : "Copiar só a senha"}
        </Button>
      </div>
      <p className="text-xs text-muted">
        Ajuste no arquivo o IP de cada regulador (<code className="font-mono">host</code>). Os demais valores já vêm do cadastro.
      </p>
      <div className="text-xs">
        <p className="mb-1 text-muted">Na caixa do gateway, depois de copiar o pacote e este arquivo:</p>
        <div className="flex items-start gap-2">
          <pre className="flex-1 overflow-x-auto rounded-md border border-line bg-surface-2 p-2 font-mono">{installCmd}</pre>
          <Button size="sm" variant="secondary" onClick={() => copy("cmd", installCmd)}>
            {copied === "cmd" ? "Copiado" : "Copiar"}
          </Button>
        </div>
        <p className="mt-1 text-muted">
          No Linux o <code className="font-mono">--config</code> move a senha para o <code className="font-mono">gateway.env</code> (0600); no Windows ela fica no
          config.yaml protegido por permissão. Passo a passo em{" "}
          <code className="font-mono">cloud-gateway/deploy/README.md</code>.
        </p>
      </div>
      <pre className="max-h-64 overflow-auto rounded-md border border-line bg-surface-2 p-3 font-mono text-xs">{yaml}</pre>
      <details className="text-sm">
        <summary className="cursor-pointer text-muted">Instalação manual: senha no gateway.env em vez do YAML</summary>
        <p className="mt-2 text-xs text-muted">
          Sem o <code className="font-mono">--config</code>: tire <code className="font-mono">username</code>/<code className="font-mono">password</code> do YAML e
          ponha em <code className="font-mono">/etc/grtd-gateway/gateway.env</code> (modo 600):
        </p>
        <div className="mt-1 flex items-start gap-2">
          <pre className="flex-1 rounded-md border border-line bg-surface-2 p-2 font-mono text-xs">{env}</pre>
          <Button size="sm" variant="secondary" onClick={() => copy("env", env)}>
            {copied === "env" ? "Copiado" : "Copiar"}
          </Button>
        </div>
      </details>
    </Modal>
  );
}
